import {
  Button,
  Icon,
} from "@equinor/e-procurement-portal-and-ts-portal-front-end-components";
import {
  mdiAlertCircleOutline,
  mdiAlertOutline,
  mdiCheck,
  mdiInformationOutline,
} from "@mdi/js";
import { Divider } from "@mui/material";
import Alert from "@mui/material/Alert";
import AlertTitle from "@mui/material/AlertTitle";
import Box from "@mui/material/Box";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import * as Sentry from "@sentry/react";
import { useCallback, useEffect, useId, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  getListAnnouncementsQueryKey,
  useListAnnouncements,
} from "../../../__generated__/orval/endpoints/announcements/announcements";
import { EntitiesAnnouncementCollectionListResponse } from "../../../__generated__/orval/models/entitiesAnnouncementCollectionListResponse.zod";
import type { ListAnnouncementsParams } from "../../../__generated__/orval/models/listAnnouncementsParams.zod";
import { useAuth } from "../../../hooks/useAuth";

const selectAnnouncements = (
  response: EntitiesAnnouncementCollectionListResponse,
) => {
  const result = EntitiesAnnouncementCollectionListResponse.safeParse(response);
  if (!result.success) {
    throw result.error;
  }
  return result.data.results;
};

const announcementModalIcon = {
  error: mdiAlertCircleOutline,
  info: mdiInformationOutline,
  success: mdiCheck,
  warning: mdiAlertOutline,
};

export const AnnouncementModal: React.FC = () => {
  const { loading, user } = useAuth();
  const { i18n, t } = useTranslation();
  const [params] = useState<ListAnnouncementsParams>(() => {
    const now = new Date().toISOString();
    return {
      active: true,
      display_type: ["modal"],
      "ends_at[gte]": now,
      "starts_at[lte]": now,
    };
  });
  const { data: announcements = [], error } = useListAnnouncements(params, {
    query: {
      enabled: !!user && !loading,
      queryKey: [
        ...getListAnnouncementsQueryKey(params),
        user?.id,
        i18n.resolvedLanguage,
      ],
      refetchOnReconnect: false,
      refetchOnWindowFocus: false,
      retry: false,
      select: selectAnnouncements,
    },
  });
  const queryContext = `${user?.id}:${i18n.resolvedLanguage}`;
  const [flow, setFlow] = useState<{ context: string; index: number } | null>(
    null,
  );
  const [hiddenAnnouncementKeys, setHiddenAnnouncementKeys] = useState<
    string[]
  >([]);
  const isFlowActive = flow?.context === queryContext;
  const currentAnnouncementIndex = isFlowActive ? (flow?.index ?? -1) : -1;
  const hasAnnouncements =
    !!user && !loading && !error && announcements.length > 0;
  const currentAnnouncement =
    hasAnnouncements && currentAnnouncementIndex >= 0
      ? (announcements[currentAnnouncementIndex] ?? null)
      : null;
  const isDialogOpen = isFlowActive && currentAnnouncement !== null;

  const startAnnouncementFlow = useCallback(
    (index: number) => {
      if (hasAnnouncements) {
        setFlow({ context: queryContext, index });
      }
    },
    [hasAnnouncements, queryContext],
  );

  const handleCloseCurrentAnnouncement = useCallback(() => {
    const nextIndex = currentAnnouncementIndex + 1;

    if (nextIndex < announcements.length) {
      setFlow({ context: queryContext, index: nextIndex });
    } else {
      setFlow(null);
    }
  }, [currentAnnouncementIndex, announcements.length, queryContext]);

  useEffect(() => {
    if (error) {
      Sentry.captureException(error);
    }
  }, [error]);

  const dialogAnnouncementModalDescriptionId = useId();
  const dialogAnnouncementModalTitleId = useId();

  if (!hasAnnouncements) {
    return null;
  }

  return (
    <>
      <Box sx={{ mt: 2, mx: 2 }}>
        {announcements.map((announcement, index) => (
          <Alert
            closeText={t("common.close")}
            icon={
              <Icon
                fontSize="inherit"
                icon={announcementModalIcon[announcement.severity]}
              />
            }
            key={announcement.id}
            onClose={() =>
              setHiddenAnnouncementKeys((currentKeys) => [
                ...currentKeys,
                `${queryContext}:${announcement.id}`,
              ])
            }
            severity={announcement.severity}
            sx={{
              "& .MuiAlert-message": {
                alignItems: "center",
                display: "flex",
                gap: 2,
                minWidth: 0,
                width: "100%",
              },
              alignItems: "center",
              display: hiddenAnnouncementKeys.includes(
                `${queryContext}:${announcement.id}`,
              )
                ? "none"
                : "flex",
              flexGrow: 1,
              mb: 2,
            }}
          >
            <AlertTitle
              sx={{ flex: 1, m: 0, minWidth: 0, overflowWrap: "anywhere" }}
            >
              {announcement.title}
            </AlertTitle>
            <Button
              color={announcement.severity}
              onClick={() => startAnnouncementFlow(index)}
              size="small"
              sx={{ flexShrink: 0 }}
            >
              {t("common.viewMore")}
            </Button>
          </Alert>
        ))}
      </Box>

      {!!currentAnnouncement && (
        <Dialog
          aria-describedby={dialogAnnouncementModalDescriptionId}
          aria-labelledby={dialogAnnouncementModalTitleId}
          fullWidth
          maxWidth="md"
          onClose={handleCloseCurrentAnnouncement}
          open={isDialogOpen}
        >
          <DialogTitle
            id={dialogAnnouncementModalTitleId}
            sx={{
              alignItems: "center",
              display: "flex",
              gap: 1,
            }}
          >
            <Icon
              color={currentAnnouncement.severity}
              icon={announcementModalIcon[currentAnnouncement.severity]}
            />
            {currentAnnouncement.title}
            {announcements.length > 1 && (
              <Box
                component="span"
                sx={{
                  fontSize: "0.875rem",
                  ml: "auto",
                  opacity: 0.7,
                }}
              >
                {currentAnnouncementIndex + 1} / {announcements.length}
              </Box>
            )}
          </DialogTitle>
          <Divider />
          <DialogContent
            id={dialogAnnouncementModalDescriptionId}
            sx={{
              alignItems: "center",
              display: "flex",
              flexDirection: "column",
              width: "100%",
            }}
          >
            <Box
              // biome-ignore lint/security/noDangerouslySetInnerHtml: The announcement content is sanitized in back-end admin and safe to use
              dangerouslySetInnerHTML={{ __html: currentAnnouncement.content }}
              sx={{ width: "100%" }}
            />
          </DialogContent>
          <Divider />
          <DialogActions>
            <Button onClick={handleCloseCurrentAnnouncement}>
              {currentAnnouncementIndex < announcements.length - 1
                ? t("common.next")
                : t("common.ok")}
            </Button>
          </DialogActions>
        </Dialog>
      )}
    </>
  );
};
